import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Skeleton,
} from '@mui/material';

const formatLabel = (key) =>
  String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const renderValue = (key, value) => {
  if (value === null || value === undefined || value === '') return null;
  if (key.startsWith('is_')) return Number(value) === 1 ? 'Yes' : 'No';
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map((v) => v?.name ?? v?.title ?? JSON.stringify(v)).join(', ');
    }
    return value.name ?? value.title ?? JSON.stringify(value);
  }
  return String(value);
};

const LeadActivityLogsDialog = ({ open, activityLogs = [], activityLogsLoading, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Lead Activity Logs {activityLogsLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
      </DialogTitle>
      <DialogContent dividers>
        {activityLogsLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[...Array(3)].map((_, i) => <Skeleton key={i} height={60} />)}
          </Box>
        ) : activityLogs.length === 0 ? (
          <Typography align="center" sx={{ py: 3 }} color="text.secondary">
            No activity logs found
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {activityLogs.map((log) => {
              const event = log?.event;
              const attributes = log?.properties?.attributes ?? {};
              const rawOldValues = log?.properties?.old_values;
              const oldValues = Array.isArray(rawOldValues) ? {} : (rawOldValues ?? {});
              const attrKeys = Object.keys(attributes);

              let color = 'default';
              if (event === 'created') color = 'success';
              if (event === 'updated') color = 'warning';
              if (event === 'deleted') color = 'error';

              return (
                <Box key={log.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={event ? event.charAt(0).toUpperCase() + event.slice(1) : 'N/A'}
                      color={color}
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {log.causer?.name ? `by ${log.causer.name}` : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      · {log.time_ago || log.created_at || ''}
                    </Typography>
                  </Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell><strong>Attribute</strong></TableCell>
                          {event === 'updated' ? (
                            <>
                              <TableCell><strong>Old Value</strong></TableCell>
                              <TableCell><strong>New Value</strong></TableCell>
                            </>
                          ) : (
                            <TableCell><strong>Value</strong></TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {attrKeys.map((key) => {
                          let label = formatLabel(key);
                          if (label === 'Id' || label === 'Uuid' || label === 'Deleted At') return null;
                          if (label.endsWith(' Id')) label = label.replace(' Id', '');

                          const oldVal = renderValue(key, oldValues[key]);
                          const newVal = renderValue(key, attributes[key]);
                          if (!oldVal && !newVal) return null;

                          return (
                            <TableRow key={key}>
                              <TableCell sx={{ fontWeight: 500 }}>{label}</TableCell>
                              {event === 'updated' && <TableCell>{oldVal ?? '—'}</TableCell>}
                              <TableCell>{newVal ?? '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadActivityLogsDialog;
